import React, { useState, useMemo, useCallback } from 'react';
import { StabeonUser, ACTIVITY_CATEGORIES, DEFAULT_AVATAR } from '../../types/user';
import { Pin } from '../../types/pin';
import { StabeonEvent } from '../../types/event';
import { AvatarDisplay } from '../avatar/AvatarDisplay';
import { IconSearch, IconPin, IconCalendar } from './Icons';

interface LocationResult {
  name: string;
  lat: number;
  lng: number;
}

interface Props {
  users: StabeonUser[];
  pins: Pin[];
  events: StabeonEvent[];
  onSelectUser: (u: StabeonUser) => void;
  onSelectPin: (p: Pin) => void;
  onSelectEvent: (e: StabeonEvent) => void;
  onGoToLocation?: (lat: number, lng: number, name: string) => void;
  onClose: () => void;
}

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

export function SearchBar({ users, pins, events, onSelectUser, onSelectPin, onSelectEvent, onGoToLocation, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [locations, setLocations] = useState<LocationResult[]>([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [showEventFilters, setShowEventFilters] = useState(false);

  const q = query.toLowerCase().trim();

  // Search users, pins, events locally
  const rawResults = useMemo(() => {
    if (!q && !categoryFilter) return { users: [], pins: [], events: [] };
    return {
      users: !q ? [] : users.filter(u =>
        (u.nickname && u.nickname.toLowerCase().includes(q)) ||
        (u.activity && u.activity.toLowerCase().includes(q)) ||
        (u.nullifierHash && u.nullifierHash.toLowerCase().includes(q))
      ).slice(0, 5),

      pins: !q ? [] : pins.filter(p =>
        (p.text && p.text.toLowerCase().includes(q)) ||
        (p.creatorNick && p.creatorNick.toLowerCase().includes(q)) ||
        (p.locationName && p.locationName.toLowerCase().includes(q)) ||
        (p.category && p.category.includes(q))
      ).slice(0, 5),

      // Events: search title, description, creatorNick AND tags
      events: events.filter(e => {
        const matchesQuery = !q || (
          (e.title && e.title.toLowerCase().includes(q)) ||
          (e.description && e.description.toLowerCase().includes(q)) ||
          (e.creatorNick && e.creatorNick.toLowerCase().includes(q)) ||
          (e.locationName && e.locationName.toLowerCase().includes(q)) ||
          (e.venueName && e.venueName.toLowerCase().includes(q)) ||
          ((e as any).tags && (e as any).tags.toLowerCase().includes(q))
        );
        const matchesCategory = !categoryFilter || e.category === categoryFilter;
        return matchesQuery && matchesCategory;
      }),
    };
  }, [q, categoryFilter, users, pins, events]);

  // Cap visible events — show more when category filter is active
  const results = useMemo(() => ({
    ...rawResults,
    events: rawResults.events.slice(0, categoryFilter ? 20 : 8),
  }), [rawResults, categoryFilter]);

  // Categories that actually have events matching the query
  const availableCategories = useMemo(() => {
    if (!q && !categoryFilter) return [];
    const cats = new Set(
      events
        .filter(e => !q || (
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          (e as any).tags?.toLowerCase().includes(q) ||
          e.locationName?.toLowerCase().includes(q) ||
          e.venueName?.toLowerCase().includes(q)
        ))
        .map(e => e.category)
    );
    return ACTIVITY_CATEGORIES.filter(c => cats.has(c.key));
  }, [q, events, categoryFilter]);

  // Search locations via Nominatim (debounced)
  const searchLocations = useCallback(async (text: string) => {
    if (text.length < 3) { setLocations([]); return; }
    setSearchingLocations(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&limit=4&accept-language=en`,
        { headers: { 'User-Agent': 'Joystance/1.0' } }
      );
      const data = await res.json();
      setLocations(data.map((d: any) => ({
        name: d.display_name?.split(',').slice(0, 3).join(',') || d.display_name,
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
      })));
    } catch { setLocations([]); }
    finally { setSearchingLocations(false); }
  }, []);

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleChange = (val: string) => {
    setQuery(val);
    setCategoryFilter(null); // reset category when query changes
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLocations(val.trim()), 500);
  };

  const hasResults = results.users.length + results.pins.length + results.events.length + locations.length > 0;
  const showFilters = (q.length > 0 || categoryFilter) && availableCategories.length > 1;

  return (
    <div className="fixed inset-0 z-[2500] flex flex-col">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 px-4 pt-4 max-w-lg mx-auto w-full">
        {/* Search input */}
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <input
              value={query}
              onChange={e => handleChange(e.target.value)}
              placeholder="Search events, people, places, tags…"
              className="input-field w-full pl-10 text-sm"
              autoFocus
            />
            <IconSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stabeon-muted" />
          </div>
          <button onClick={onClose} className="text-stabeon-muted text-sm font-medium px-2 py-3">Cancel</button>
        </div>

        {/* Category filter chips — shown when events exist */}
        {showFilters && (
          <div className="flex gap-1.5 mt-2.5 overflow-x-auto scrollbar-hide pb-0.5">
            <button
              onClick={() => setCategoryFilter(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                !categoryFilter
                  ? 'bg-stabeon-accent text-white'
                  : 'bg-white/6 text-stabeon-muted border border-white/10'
              }`}
            >
              All ({rawResults.events.length})
            </button>
            {availableCategories.map(cat => {
              const count = rawResults.events.filter(e => e.category === cat.key).length;
              const active = categoryFilter === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setCategoryFilter(active ? null : cat.key)}
                  className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-stabeon-accent text-white'
                      : 'bg-white/6 text-stabeon-muted border border-white/10'
                  }`}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  {count > 0 && <span className={`ml-0.5 ${active ? 'text-white/70' : 'text-stabeon-muted/60'}`}>·{count}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        {(q || categoryFilter) && (
          <div className="mt-3 glass rounded-2xl max-h-[65vh] overflow-y-auto">
            {!hasResults && !searchingLocations && (
              <p className="text-stabeon-muted text-sm text-center py-8">
                No results{q ? ` for "${query}"` : ''}{categoryFilter ? ` in ${ACTIVITY_CATEGORIES.find(c=>c.key===categoryFilter)?.label}` : ''}
              </p>
            )}

            {searchingLocations && locations.length === 0 && (
              <div className="flex justify-center py-4">
                <div className="w-4 h-4 rounded-full border-2 border-stabeon-accent border-t-transparent animate-spin" />
              </div>
            )}

            {/* Locations */}
            {locations.length > 0 && (
              <div className="p-3">
                <p className="text-stabeon-muted text-[10px] font-semibold uppercase tracking-widest mb-2 px-1">Places</p>
                {locations.map((loc, i) => (
                  <button
                    key={i}
                    onClick={() => { onGoToLocation?.(loc.lat, loc.lng, loc.name); onClose(); }}
                    className="flex items-center gap-3 p-2.5 rounded-xl w-full text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-stabeon-accent/15 flex items-center justify-center flex-shrink-0">
                      <IconPin size={14} className="text-stabeon-accent" />
                    </div>
                    <p className="text-stabeon-text text-sm truncate flex-1">{loc.name}</p>
                  </button>
                ))}
              </div>
            )}

            {/* People */}
            {results.users.length > 0 && (
              <div className="p-3 border-t border-white/6">
                <p className="text-stabeon-muted text-[10px] font-semibold uppercase tracking-widest mb-2 px-1">People</p>
                {results.users.map(u => (
                  <button
                    key={u.nullifierHash}
                    onClick={() => { onSelectUser(u); onClose(); }}
                    className="flex items-center gap-3 p-2.5 rounded-xl w-full text-left hover:bg-white/5 transition-colors"
                  >
                    <AvatarDisplay avatar={u.avatar || DEFAULT_AVATAR} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className="text-stabeon-text text-sm font-medium truncate">{u.nickname || 'Anonymous'}</p>
                      <p className="text-stabeon-muted text-xs truncate">{u.activity || 'Online'}</p>
                    </div>
                    {u.isOnline && <span className="w-1.5 h-1.5 rounded-full bg-stabeon-online" />}
                  </button>
                ))}
              </div>
            )}

            {/* Pins */}
            {results.pins.length > 0 && (
              <div className="p-3 border-t border-white/6">
                <p className="text-stabeon-muted text-[10px] font-semibold uppercase tracking-widest mb-2 px-1">Pins</p>
                {results.pins.map(p => {
                  const cat = ACTIVITY_CATEGORIES.find(c => c.key === p.category);
                  return (
                    <button
                      key={p.id}
                      onClick={() => { onSelectPin(p); onClose(); }}
                      className="flex items-center gap-3 p-2.5 rounded-xl w-full text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-xl w-8 text-center">{cat?.emoji || '📍'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-stabeon-text text-sm truncate">{p.text || cat?.label || 'Pin'}</p>
                        <p className="text-stabeon-muted text-xs">{p.locationName || p.creatorNick} · {timeAgo(p.createdAt)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Events */}
            {results.events.length > 0 && (
              <div className="p-3 border-t border-white/6">
                <div className="flex items-center justify-between mb-2 px-1">
                  <p className="text-stabeon-muted text-[10px] font-semibold uppercase tracking-widest">
                    Events {rawResults.events.length > 8 && !categoryFilter ? `(showing 8 of ${rawResults.events.length})` : `(${rawResults.events.length})`}
                  </p>
                  {rawResults.events.length > 8 && !categoryFilter && (
                    <p className="text-stabeon-accent text-[10px]">Filter by category ↑</p>
                  )}
                </div>
                {results.events.map(e => {
                  const cat = ACTIVITY_CATEGORIES.find(c => c.key === e.category);
                  const tags: string[] = (e as any).tags
                    ? (e as any).tags.split(',').map((t: string) => t.trim()).filter(Boolean).slice(0, 3)
                    : [];
                  const now = Date.now();
                  const isLive = e.startsAt <= now && e.endsAt > now;
                  return (
                    <button
                      key={e.id}
                      onClick={() => { onSelectEvent(e); onClose(); }}
                      className="flex items-start gap-3 p-2.5 rounded-xl w-full text-left hover:bg-white/5 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg mt-0.5"
                        style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                        {cat?.emoji || '🎉'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-stabeon-text text-sm font-medium truncate flex-1">{e.title}</p>
                          {isLive && (
                            <span className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">LIVE</span>
                          )}
                        </div>
                        <p className="text-stabeon-muted text-xs mt-0.5">
                          {new Date(e.startsAt).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {e.venueName ? ` · ${e.venueName}` : e.locationName ? ` · ${e.locationName}` : ''}
                        </p>
                        {tags.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {tags.map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-stabeon-accent/10 text-stabeon-accent/80">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
